<template>
  <div>
    <!-- INVOICE PREVIEWER -->
    <v-dialog
      max-width="1024"
      :fullscreen="
        this.$vuetify.breakpoint.md ||
        this.$vuetify.breakpoint.sm ||
        this.$vuetify.breakpoint.xs
      "
      v-model="showInvoicesPreviewer"
      lazy
      scrollable
    >
      <v-card>
        <v-toolbar
          tile
          card
          flat
          height="36"
          color="theme_primary"
          class="white--text"
        >
          <v-toolbar-side-icon>
            <v-icon small color="white">mdi-file-document</v-icon>
          </v-toolbar-side-icon>
          <v-spacer></v-spacer>
          <v-toolbar-title class="white-text text-capitalize subheading"
            >Invoices</v-toolbar-title
          >
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text class="pa-0">
          <fileListViewer title="Invoices" :documents="invoices" />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="showInvoicesPreviewer = false" flat color="red"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showReconciliationDialog" width="640" lazy>
      <reconciliationModal
        @closeDialog="closeReconciliationDialog"
        :bankReconciliationId="currentReconciliationId"
      />
    </v-dialog>

    <paginatedTable
      :headers="headers"
      store="commonStore"
      getter="bankReconciliationsTable"
      :pagination="pagination"
      :graphqlQuery="getGraphqlQuery()"
      resourceName="bank_reconciliations"
      searchLabel="Search Reconciliations..."
      :filters="filters"
      :externalFilterString="invoicesFilter('bank_reconciliations')"
      :exportExcel="true"
    >
      <template slot="myItems" slot-scope="props">
        <tr @click="openReconciliationDialog(props.item)">
          <!-- DATE -->
          <td class="text-xs-left">
            {{ props.item.created_at | friendlyDateFullYear }}
          </td>

          <!-- COMPANY NAME -->
          <td class="text-xs-left">{{ getCompanyName(props.item) }}</td>
          <!-- INVOICES -->
          <td
            class="text-xs-left"
            @click.stop="InvoicesPreviewer(props.item.bank_invoices)"
          >
            <span v-if="!props.item.bank_invoices.length" class="caption">
              <v-icon color="blue" small class="pr-2">info</v-icon>No invoices.
            </span>
            <span v-else>
              <a>{{ getInvoicesLabel(props.item.bank_invoices) }}</a>
            </span>
          </td>
          <!-- INVOICES TOTAL AMOUNT -->
          <td class="text-xs-left">
            {{
              inCurrency(props.item.currency.code, props.item.invoices_amount)
            }}
          </td>
          <!-- TRANSACTIONS -->
          <td class="text-xs-left">
            <span v-if="!props.item.bank_transactions.length" class="caption">
              <v-icon color="blue" small class="pr-2">info</v-icon>No
              transactions.
            </span>
            <span v-else>
              <v-tooltip bottom min-width="600" width="600">
                <span slot="activator">
                  <v-icon small size="14" class="pr-1 grey--text">help</v-icon>
                  {{ getTransactionsLabel(props.item.bank_transactions) }}
                </span>
                <span>
                  <h3 class="pa-4">
                    This reconciliation contains
                    {{ getTransactionsLabel(props.item.bank_transactions) }}:
                  </h3>
                  <template v-for="transaction in props.item.bank_transactions">
                    <v-container
                      style="width: 500px;"
                      grid-list-xl
                      :key="transaction.id"
                    >
                      <v-layout row wrap>
                        <v-flex xs4>
                          <span>
                            <v-icon small dark class="pr-2"
                              >mdi-bank-transfer</v-icon
                            >
                            <span>{{ transaction.reference }}</span>
                            <br />
                          </span>
                        </v-flex>
                        <v-flex xs4>{{
                          transaction.operation_type == "credit"
                            ? "+"
                            : "-" +
                              inCurrency(
                                props.item.currency.code,
                                transaction.amount
                              )
                        }}</v-flex>
                        <v-flex xs4>
                          {{ timeToDate(transaction.created_at) }}
                        </v-flex>
                      </v-layout>
                    </v-container>
                  </template>
                </span>
              </v-tooltip>
            </span>
          </td>
          <!-- TRANSACTION AMOUNT -->
          <td class="text-xs-left">
            {{
              inCurrency(
                props.item.currency.code,
                props.item.transactions_amount
              )
            }}
          </td>

          <!-- REMAINING -->
          <td class="text-xs-left">
            {{ inCurrency(props.item.currency.code, props.item.remaining) }}
          </td>
          <td class="text-xs-left">
            <div @click.stop="openReconciliationDialog(props.item)">
              <v-chip>
                <v-icon
                  size="14"
                  class="pr-1"
                  :color="
                    statusColor(statusLabel(props.item.reconciliation_status))
                  "
                  >{{
                    statusIcon(statusLabel(props.item.reconciliation_status))
                  }}</v-icon
                >
                {{ statusLabel(props.item.reconciliation_status) }}
              </v-chip>
            </div>
          </td>

          <td class="text-xs-left">
            <v-layout row justify-center>
              <v-tooltip bottom>
                <span>Remove</span>
                <destroyModale
                  slot="activator"
                  resourceName="bank_reconciliation"
                  :itemId="props.item.id"
                  warningText="You are going to remove this reconciliation permanently."
                  small
                  color="ctcRed"
                  :icon="true"
                  :small_icon="true"
                  :fab="false"
                />
              </v-tooltip>
            </v-layout>
          </td>
        </tr>
      </template>
    </paginatedTable>
  </div>
</template>

<script>
import currency from "../../../mixins/currency";
import date from "../../../mixins/date";
import paginatedTable from "../../shared/paginatedTable";
import PAGINATED_BANK_RECONCILIATIONS_INDEX from "../../../graphql/bank_reconciliations/paginated_index.gql";
import accountingFilters from "../../../mixins/accountingFilters.js";
import fileListViewer from "../../shared/fileListViewer";
import pluralize from "pluralize";
import reconciliationModal from "./reconciliationModal";
import accountingStatus from "../../../mixins/accountingStatus.js";

export default {
  name: "transactionsIndex",
  components: {
    paginatedTable,
    fileListViewer,
    reconciliationModal,
  },
  mixins: [accountingFilters, currency, date, accountingStatus],
  data() {
    return {
      showReconciliationDialog: false,
      currentReconciliationId: "",
      showInvoicesPreviewer: false,
      invoices: [],
      filters: [
        {
          field: "company_id",
          label: "Customer / Supplier",
          getter: "companies",
          itemText: "name",
          type: "resource_id",
        },
        {
          field: "bank_invoices.designation",
          label: "Designation",
          getter: "bank_invoices",
          itemText: "designation",
          type: "text",
        },
        {
          field: "remaining",
          label: "Remaining",
          type: "float",
          operation: ">",
        },
        {
          field: "invoices_amount",
          label: "Invoices Amount",
          type: "float",
          operation: ">",
        },
        {
          field: "transactions_amount",
          label: "Transactions Amount",
          type: "float",
          operation: ">",
        },
        {
          field: "reconciliation_status",
          label: "Reconciliation",
          type: "enum",
          resources: [
            { text: "All", value: null },
            { text: "Done", value: "done" },
            { text: "Partial", value: "partial" },
          ],
        },
        {
          field: "created_at",
          label: "Created Before",
          type: "date",
          operation: "<=",
        },
        {
          field: "created_at",
          label: "Created After",
          type: "date",
          operation: ">=",
        },
      ],
      pagination: {
        descending: true,
        rowsPerPage: 25,
        sortBy: "created_at",
      },
      headers: [
        {
          text: "Created At",
          align: "left",
          sortable: true,
          value: "created_at",
        },
        {
          text: "Company",
          align: "left",
          sortable: true,
          value: "company.name",
        },
        {
          text: "Invoices",
          align: "left",
          value: "",
        },
        {
          text: "Invoices Amount",
          align: "left",
          sortable: true,
          value: "invoices_amount",
        },
        {
          text: "Transactions",
          align: "left",
          value: "",
        },
        {
          text: "Transactions Amount",
          align: "left",
          sortable: true,
          value: "transactions_amount",
        },
        {
          text: "Remaining",
          align: "left",
          sortable: true,
          value: "remaining",
        },
        {
          text: "Status",
          align: "left",
          sortable: true,
          value: "reconciliation_status",
        },
        {
          text: "Actions",
          align: "center",
          sortable: false,
          width: "5%",
        },
      ],
    };
  },
  methods: {
    getCompanyName(reconciliation) {
      if (reconciliation.company) return reconciliation.company.name;
      if (reconciliation.bank_invoices.length === 0)
        if (reconciliation.bank_transactions.length === 0)
          return "Unregistered company";
        else
          return reconciliation.bank_transactions.map((e) => e.company.name).join(", ");
      return reconciliation.bank_invoices.map((e) => e.company_name).join(", ");
    },
    getGraphqlQuery() {
      return PAGINATED_BANK_RECONCILIATIONS_INDEX;
    },
    InvoicesPreviewer(bank_invoices) {
      this.showInvoicesPreviewer = true;
      this.invoices = bank_invoices.map((bi) => {
        let sign = bi.operation_type == "credit" ? "+" : "-";
        let amount_str = this.inCurrencyFromId(bi.currency_id, bi.amount);
        return {
          name: `${bi.designation}  (${sign}${amount_str})`,
          url: bi.document_url,
        };
      });
    },
    getInvoicesLabel(invoices) {
      return `${invoices.length} ${pluralize("Invoice", invoices.length)}`;
    },
    getTransactionsLabel(transactions) {
      return `${transactions.length} ${pluralize(
        "Transaction",
        transactions.length
      )}`;
    },
    closeReconciliationDialog() {
      this.showReconciliationDialog = false;
    },
    openReconciliationDialog(item) {
      this.currentReconciliationId = item.id;
      this.showReconciliationDialog = true;
    },
  },
};
</script>

<style lang="stylus" scoped>
td {
  width: 1%;
  white-space: nowrap;
}
</style>
